import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Send, Plane } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { Pill } from './ui/Pill';
import { CURRENCIES } from '../lib/catalogs';
import {
  useExpenseReport,
  useCreateExpenseReport,
  useAddExpenseLine,
  useSubmitExpenseReport,
  useExpenseStatus,
  usePerDiemRates,
  PER_DIEM_REGIONS,
  PER_DIEM_ROLES,
} from '../hooks/useExpenses';
import { formatAmount, formatDate } from '../lib/format';
import { useCan } from '../hooks/useCan';

const CATEGORIES = ['transport', 'meals', 'lodging', 'supplies', 'entertainment', 'other'];
const TAX_RATES = [0, 8, 10] as const;
/** Categories that map to a travel / per-diem allowance lookup. */
const PER_DIEM_CATEGORIES = new Set(['transport', 'meals', 'lodging']);
/** Which per-diem field a category pre-fills from. */
const PER_DIEM_FIELD: Record<string, 'lodgingCapCents' | 'mealsPerDiemCents' | 'transportAllowanceCents'> = {
  lodging: 'lodgingCapCents',
  meals: 'mealsPerDiemCents',
  transport: 'transportAllowanceCents',
};

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function ExpenseReportCreateDrawer({
  wsId,
  open,
  onOpenChange,
  onCreated,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateExpenseReport(wsId);
  const [title, setTitle] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [error, setError] = useState<string | null>(null);
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('expenses.newReport')}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={async () => {
              setError(null);
              if (!title.trim()) {
                setError(t('expenses.errorTitleRequired'));
                return;
              }
              try {
                const r = await create.mutateAsync({ title: title.trim(), currency });
                setTitle('');
                onCreated?.(r.id);
                onOpenChange(false);
              } catch (err: any) {
                setError(err?.response?.data?.message ?? err?.message ?? 'Create failed');
              }
            }}
            disabled={create.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <FormField label={t('expenses.title2')}>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('expenses.titlePlaceholder')}
            className={inputCn}
          />
        </FormField>
        <FormField label={t('inbox.field.currency')}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCn}>
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.symbol}
              </option>
            ))}
          </select>
        </FormField>
      </div>
    </Drawer>
  );
}

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  draft: 'muted',
  submitted: 'accent',
  approved: 'success',
  rejected: 'warn',
  paid: 'success',
};

export function ExpenseReportDrawer({
  wsId,
  reportId,
  open,
  onOpenChange,
}: {
  wsId: string;
  reportId: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const { data, isLoading } = useExpenseReport(wsId, reportId);
  const addLine = useAddExpenseLine(wsId);
  const submit = useSubmitExpenseReport(wsId);
  const status = useExpenseStatus(wsId);
  const today = new Date().toISOString().slice(0, 10);
  const [showAdd, setShowAdd] = useState(false);
  const [occurredOn, setOccurredOn] = useState(today);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('transport');
  const [amountCents, setAmountCents] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [taxRate, setTaxRate] = useState(10);
  const [jpQualified, setJpQualified] = useState(false);
  const [receipt, setReceipt] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [perDiemRegion, setPerDiemRegion] = useState('jp_domestic');
  const [perDiemRole, setPerDiemRole] = useState('staff');

  const isPerDiem = PER_DIEM_CATEGORIES.has(category);
  const perDiem = usePerDiemRates(
    wsId,
    isPerDiem ? perDiemRegion : undefined,
    isPerDiem ? perDiemRole : undefined,
  );

  const report = data?.report;
  const lines = data?.lines ?? [];

  const applyPerDiem = () => {
    if (!perDiem.data) return;
    const field = PER_DIEM_FIELD[category];
    const cents = field ? perDiem.data[field] : 0;
    setAmountCents(String(cents));
    setCurrency(perDiem.data.currency);
  };

  const onAddLine = async () => {
    if (!report) return;
    setError(null);
    const cents = Number(amountCents);
    if (!description.trim() || !Number.isFinite(cents) || cents <= 0) {
      setError(t('expenses.errorLineInvalid'));
      return;
    }
    try {
      await addLine.mutateAsync({
        reportId: report.id,
        occurredOn,
        description: description.trim(),
        category,
        amountCents: cents,
        currency,
        taxRate,
        jpQualified,
        receipt: receipt ?? undefined,
      });
      setDescription('');
      setAmountCents('');
      setReceipt(null);
      setShowAdd(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Add failed');
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={report?.title ?? '—'}
      description={report ? `${report.currency} · ${t(`common.${report.status}`)}` : undefined}
      footer={
        report && canMutate ? (
          <div className="flex justify-end gap-2">
            {report.status === 'draft' && (
              <>
                <button
                  type="button"
                  onClick={() => setShowAdd((s) => !s)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                >
                  <Plus size={12} />
                  {t('expenses.addLine')}
                </button>
                <button
                  type="button"
                  onClick={() => submit.mutate(report.id)}
                  disabled={submit.isPending || lines.length === 0}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                >
                  <Send size={12} />
                  {t('expenses.submit')}
                </button>
              </>
            )}
            {report.status === 'submitted' && (
              <>
                <button type="button" onClick={() => status.mutate({ id: report.id, status: 'rejected' })} disabled={status.isPending}
                  className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium text-warn-600 hover:bg-warn-50 dark:hover:bg-warn-900/20 disabled:opacity-50">
                  {t('common.reject')}
                </button>
                <button type="button" onClick={() => status.mutate({ id: report.id, status: 'approved' })} disabled={status.isPending}
                  className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
                  {t('common.approve')}
                </button>
              </>
            )}
            {report.status === 'approved' && (
              <button type="button" onClick={() => status.mutate({ id: report.id, status: 'paid' })} disabled={status.isPending}
                className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50">
                {t('expenses.markPaid')}
              </button>
            )}
          </div>
        ) : null
      }
    >
      {isLoading || !report ? (
        <div className="flex items-center gap-2 text-sm text-ink-500 py-10 justify-center">
          <Loader2 size={14} className="animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-500">{t('common.status')}</div>
              <Pill tone={STATUS_TONE[report.status] ?? 'neutral'}>{t(`common.${report.status}`)}</Pill>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-ink-500">{t('inbox.field.total')}</div>
              <div className="num text-lg font-semibold">
                {formatAmount(report.total_cents, report.currency, i18n.language)}
              </div>
            </div>
          </div>

          {showAdd && report.status === 'draft' && (
            <section className="rounded-lg border border-ink-200 dark:border-ink-800 p-3 bg-ink-50 dark:bg-ink-900/50 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField label={t('cards.occurredAt')}>
                  <input
                    type="date"
                    value={occurredOn}
                    onChange={(e) => setOccurredOn(e.target.value)}
                    className={inputCn}
                  />
                </FormField>
                <FormField label={t('expenses.category')}>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCn}>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {t(`expenses.cat.${c}`)}
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
              <FormField label={t('inbox.col.description')}>
                <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCn} />
              </FormField>
              {isPerDiem && (
                <div className="rounded-md border border-accent-200 dark:border-accent-800 bg-accent-50/50 dark:bg-accent-900/15 p-2.5 space-y-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-accent-700 dark:text-accent-300">
                    <Plane size={12} /> {t('expense.perDiemTitle')}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <FormField label={t('expense.perDiemRegion')}>
                      <select value={perDiemRegion} onChange={(e) => setPerDiemRegion(e.target.value)} className={inputCn}>
                        {PER_DIEM_REGIONS.map((r) => (
                          <option key={r} value={r}>{t(`expense.perDiemRegions.${r}`)}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label={t('expense.perDiemRole')}>
                      <select value={perDiemRole} onChange={(e) => setPerDiemRole(e.target.value)} className={inputCn}>
                        {PER_DIEM_ROLES.map((r) => (
                          <option key={r} value={r}>{t(`expense.perDiemRoles.${r}`)}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-ink-500">
                      {perDiem.isLoading
                        ? t('common.loading')
                        : perDiem.data
                        ? t('expense.perDiemAllowance', {
                            amount: formatAmount(
                              perDiem.data[PER_DIEM_FIELD[category]] ?? 0,
                              perDiem.data.currency,
                              i18n.language,
                            ),
                          })
                        : ''}
                    </span>
                    <button
                      type="button"
                      onClick={applyPerDiem}
                      disabled={!perDiem.data}
                      className="px-2.5 py-1 rounded-md border border-accent-300 dark:border-accent-700 text-[11px] font-medium text-accent-700 dark:text-accent-300 hover:bg-accent-100 dark:hover:bg-accent-900/30 disabled:opacity-40"
                    >
                      {t('expense.perDiemPrefill')}
                    </button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <FormField label={t('cards.amountCents')}>
                  <input
                    type="number"
                    value={amountCents}
                    onChange={(e) => setAmountCents(e.target.value)}
                    className={inputCn}
                  />
                </FormField>
                <FormField label={t('inbox.field.currency')}>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCn}>
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label={t('invoicesAr.taxRate')}>
                  <select
                    value={taxRate}
                    onChange={(e) => setTaxRate(Number(e.target.value))}
                    className={inputCn}
                  >
                    {TAX_RATES.map((r) => (
                      <option key={r} value={r}>
                        {r}%
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  id="jp-q"
                  checked={jpQualified}
                  onChange={(e) => setJpQualified(e.target.checked)}
                  className="h-4 w-4 rounded border-ink-300 dark:border-ink-700 accent-accent-600"
                />
                <label htmlFor="jp-q">{t('expenses.jpQualified')}</label>
              </div>
              <FormField label={t('expenses.receipt')}>
                <input
                  type="file"
                  accept="application/pdf,image/*,text/html,text/plain,.pdf,.html,.htm,.txt,.png,.jpg,.jpeg"
                  onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
                  className="text-xs"
                />
              </FormField>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onAddLine}
                  disabled={addLine.isPending}
                  className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                >
                  {t('common.save')}
                </button>
              </div>
            </section>
          )}

          <section>
            <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">{t('inbox.lineItems')}</h3>
            {lines.length === 0 ? (
              <div className="text-sm text-ink-500 py-6 text-center rounded-lg border border-dashed border-ink-200 dark:border-ink-800">
                {t('expenses.noLines')}
              </div>
            ) : (
              <ul className="divide-y divide-ink-200 dark:divide-ink-800 rounded-lg border border-ink-200 dark:border-ink-800">
                {lines.map((l) => (
                  <li key={l.id} className="px-3 py-2 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{l.description}</div>
                      <div className="text-xs text-ink-500">
                        {formatDate(l.occurred_on, i18n.language)} · {l.category}
                        {l.receipt_url ? ' · 📎' : ''}
                      </div>
                    </div>
                    <span className="num">{formatAmount(l.amount_cents, l.currency, i18n.language)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}
